import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTranslations } from '@/i18n';
import type { ReactNode } from 'react';

export type MissingExpenseType = 'accommodation' | 'transport' | 'fuel';

export interface MissingExpenseEntry {
  cityId: number;
  cityName: string;
  missing: MissingExpenseType[];
}

interface MissingExpensesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entries: MissingExpenseEntry[];
}

function ExpenseBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-[#f9f9f9] px-3 py-1 text-sm leading-5 text-[#292929]">
      {children}
    </span>
  );
}

export default function MissingExpensesDialog({
  isOpen,
  onClose,
  entries,
}: MissingExpensesDialogProps) {
  const tTripsValidation = useTranslations('trips.sendValidation');
  const tExpenses = useTranslations('expenses');

  const resolveLabel = (type: MissingExpenseType) => {
    switch (type) {
      case 'accommodation':
        return tExpenses('accommodation');
      case 'transport':
        return tExpenses('transport');
      case 'fuel':
        return tExpenses('fuel');
      default:
        return type;
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-[30rem] gap-0 rounded-3xl border-0 p-0"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">{tTripsValidation('title')}</DialogTitle>

        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="text-[18px] leading-7 font-bold text-black">
            {tTripsValidation('title')}
          </h2>
          <Button
            onClick={onClose}
            variant="unstyled"
            size="none"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f1f1f1] transition-colors hover:bg-[#e4e4e4]"
            aria-label={tTripsValidation('close')}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
        </div>

        <div className="px-6 pb-4">
          <p className="mb-4 text-base leading-6 text-[#292929]">
            {tTripsValidation('description')}
          </p>

          <div className="flex flex-col gap-4">
            {entries.map((entry) => (
              <div key={entry.cityId} className="rounded-2xl border border-[#f1f1f1] px-4 py-3">
                <div className="mb-2 text-[15px] leading-6 font-medium text-[#292929]">
                  {entry.cityName}
                </div>
                <div className="flex flex-wrap gap-2">
                  {entry.missing.map((item) => (
                    <ExpenseBadge key={`${entry.cityId}-${item}`}>
                      {resolveLabel(item)}
                    </ExpenseBadge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 pb-6">
          <Button
            type="button"
            onClick={onClose}
            className="h-auto rounded-full bg-[#242424] px-4 py-3 text-[15px] font-medium text-white transition-colors hover:bg-[#1a1a1a]"
          >
            {tTripsValidation('close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
